import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TRPCClientError } from '@trpc/client';
import type {
  AccountsPayableReportInput,
  AccountsPayableReportResponse,
  AccountsPayableReportRow,
  Vendor,
} from '@repo/api-contract';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import client from '../../util/api-client';
import { Routes } from '../../util/constants';
import { pushToast, useDispatch } from '../../state';
import { serverError } from '../../components/modals/constants/errors';
import { openSearchVendorModal } from '../../state/vendorSearchModalSlice';
import {
  EMPTY_LEADING_GRID_COLUMNS,
  usePersistedDataGridColumnPreferences,
} from '../../util/hooks';
import type { DataGridTableId } from '../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../../components/dataGrid/DataGridTranslatedColumnHeader';
import hexToRGBA from '../../util/converters/hexToRGBA';

const TABLE_ID = 'accounts-payable-report' satisfies DataGridTableId;
const KEY_ACCOUNTS_PAYABLE_REPORT_PAGE =
  'team.Components.AccountsPayableReportPage';
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const AGING_MARKS = [
  { value: 0, label: 'Current' },
  { value: 91, label: '91+' },
];

function dateInputToUnix(value: string, endOfDay = false): number | undefined {
  if (!value) return undefined;
  const [year = 0, month = 1, day = 1] = value.split('-').map(Number);
  const date = new Date(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  );
  return Math.floor(date.getTime() / 1000);
}

function formatDate(value: number) {
  return new Date(value * 1000).toLocaleDateString();
}

function formatAgingRangeLabel(range: number[]) {
  const min = range[0] ?? 0;
  const max = range[1] ?? 0;
  if (min <= 0 && max <= 0) return 'Current';
  if (max >= 91) return `${min}-91+`;
  return `${min}-${max}`;
}

export default function AccountsPayableReport() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { t } = useTranslation([], {
    keyPrefix: KEY_ACCOUNTS_PAYABLE_REPORT_PAGE,
  });
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filterByVendor, setFilterByVendor] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [filterByAging, setFilterByAging] = useState(false);
  const [agingRange, setAgingRange] = useState<number[]>([1, 30]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [report, setReport] = useState<AccountsPayableReportResponse | null>(
    null,
  );
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const handleError = useCallback(
    (error: unknown) => {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    },
    [dispatch],
  );

  const buildReportInput = useCallback(
    (page: number, pageSize: number): AccountsPayableReportInput => ({
      vendorCode:
        filterByVendor && selectedVendor ? selectedVendor.code : undefined,
      aging: filterByAging || undefined,
      dateFrom: dateInputToUnix(dateFrom),
      dateTo: dateInputToUnix(dateTo, true),
      size: pageSize,
      page,
    }),
    [filterByVendor, selectedVendor, filterByAging, dateFrom, dateTo],
  );

  const downloadInput = useMemo(
    () => ({
      vendorCode:
        filterByVendor && selectedVendor ? selectedVendor.code : undefined,
      aging: filterByAging || undefined,
      dateFrom: dateInputToUnix(dateFrom),
      dateTo: dateInputToUnix(dateTo, true),
    }),
    [filterByVendor, selectedVendor, filterByAging, dateFrom, dateTo],
  );

  const loadReport = useCallback(
    async (input: AccountsPayableReportInput) => {
      if (
        input.dateFrom != null &&
        input.dateTo != null &&
        input.dateFrom > input.dateTo
      ) {
        dispatch(
          pushToast({ message: t('From must be before To'), type: 'error' }),
        );
        return;
      }

      setLoadingReport(true);
      try {
        const response = await client.getAccountsPayableReport.query(input);
        setReport(response);
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingReport(false);
      }
    },
    [dispatch, handleError, t],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadVendors() {
      setLoadingVendors(true);
      try {
        const response = await client.searchVendors.query({
          query: '',
          searchBy: 'vendorName',
          size: 100,
          page: 0,
        });
        if (!cancelled) {
          setVendors(response.results);
        }
      } catch (error) {
        if (cancelled) return;
        handleError(error);
      } finally {
        if (!cancelled) {
          setLoadingVendors(false);
        }
      }
    }

    loadVendors();

    return () => {
      cancelled = true;
    };
  }, [handleError]);

  useEffect(() => {
    void loadReport(buildReportInput(0, paginationModel.pageSize));
    // Run the default unfiltered report once on page open.
  }, []);

  const rows = useMemo(
    (): AccountsPayableReportRow[] => report?.results ?? [],
    [report],
  );

  const handleGenerate = () => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
    loadReport(buildReportInput(0, paginationModel.pageSize));
  };

  const handleClearFilters = () => {
    setFilterByVendor(false);
    setSelectedVendor(null);
    setFilterByAging(false);
    setAgingRange([1, 30]);
    setDateFrom('');
    setDateTo('');
  };

  const handleDownload = async () => {
    if (!report) return;
    setDownloading(true);
    try {
      const response =
        await client.downloadAccountsPayableReport.query(downloadInput);
      window.open(response.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      handleError(error);
    } finally {
      setDownloading(false);
    }
  };

  const baseColumns = useMemo(
    (): GridColDef[] => [
      {
        field: 'vendorCode',
        headerName: 'Vendor Code',
        flex: 1,
        minWidth: 120,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Code"
            keyPrefix={KEY_ACCOUNTS_PAYABLE_REPORT_PAGE}
            params={params}
          />
        ),
      },
      {
        field: 'vendorName',
        headerName: 'Vendor Name',
        flex: 1,
        minWidth: 160,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Name"
            keyPrefix={KEY_ACCOUNTS_PAYABLE_REPORT_PAGE}
            params={params}
          />
        ),
      },
      {
        field: 'invoiceCode',
        headerName: 'Invoice No.',
        flex: 1,
        minWidth: 120,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Invoice No."
            keyPrefix={KEY_ACCOUNTS_PAYABLE_REPORT_PAGE}
            params={params}
          />
        ),
      },
      {
        field: 'invoiceDate',
        headerName: 'Invoice Date',
        flex: 1,
        minWidth: 120,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Invoice Date"
            keyPrefix={KEY_ACCOUNTS_PAYABLE_REPORT_PAGE}
            params={params}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<AccountsPayableReportRow, number>,
        ) => <span>{formatDate(params.value ?? 0)}</span>,
      },
      {
        field: 'dueDate',
        headerName: 'Due Date',
        flex: 1,
        minWidth: 120,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Due Date"
            keyPrefix={KEY_ACCOUNTS_PAYABLE_REPORT_PAGE}
            params={params}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<AccountsPayableReportRow, number>,
        ) => <span>{formatDate(params.value ?? 0)}</span>,
      },
      {
        field: 'pastDueDays',
        headerName: 'Past Due Days',
        flex: 1,
        minWidth: 120,
        type: 'number',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Past Due Days"
            keyPrefix={KEY_ACCOUNTS_PAYABLE_REPORT_PAGE}
            params={params}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<AccountsPayableReportRow, number>,
        ) => {
          const value = params.value ?? 0;
          return (
            <Typography
              component="span"
              color={value > 0 ? 'error.main' : 'inherit'}
              fontSize="inherit"
            >
              {value}
            </Typography>
          );
        },
      },
    ],
    [],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: EMPTY_LEADING_GRID_COLUMNS,
  });

  const columns = useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        bgcolor: '#fff',
        p: { xs: 2, md: 4 },
        overflow: 'auto',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          minHeight: 640,
          maxWidth: 1160,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
            {t('Accounts Payable Report')}
          </Typography>
        </Box>
        <Divider />

        <Box
          sx={{
            p: { xs: 2, md: 5 },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('Generate Report by')}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr auto' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <Box>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterByVendor}
                      onChange={(event) => {
                        setFilterByVendor(event.target.checked);
                        if (!event.target.checked) {
                          setSelectedVendor(null);
                        }
                      }}
                    />
                  }
                  label={t('Vendor')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterByAging}
                      onChange={(event) =>
                        setFilterByAging(event.target.checked)
                      }
                    />
                  }
                  label={t('Aging')}
                />
              </Stack>
              {filterByVendor && (
                <>
                  <Autocomplete
                    size="small"
                    sx={{ mt: 1 }}
                    loading={loadingVendors}
                    options={vendors}
                    value={selectedVendor}
                    getOptionLabel={(option) =>
                      `${option.code} - ${option.details.companyName}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.VendorId === value.VendorId
                    }
                    onChange={(_, value) => setSelectedVendor(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('Search for Vendor...')}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <SearchIcon color="action" />,
                          endAdornment: (
                            <>
                              {loadingVendors ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Link
                    component="button"
                    underline="hover"
                    sx={{ mt: 1, ml: 1, fontSize: 14 }}
                    onClick={() =>
                      dispatch(
                        openSearchVendorModal({
                          multiSelect: false,
                          onImport: (selectedVendors) => {
                            const selected = selectedVendors[0];
                            if (selected) {
                              setSelectedVendor(selected);
                            }
                          },
                        }),
                      )
                    }
                  >
                    {t('Browse Vendor')}
                  </Link>
                </>
              )}
              {filterByAging && (
                <Box sx={{ mt: 2, px: 1, maxWidth: 360 }}>
                  <Slider
                    value={agingRange}
                    onChange={(_, value) => setAgingRange(value as number[])}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value, index) =>
                      index === 0
                        ? formatAgingRangeLabel(agingRange)
                        : undefined
                    }
                    min={0}
                    max={91}
                    marks={AGING_MARKS.map((mark) => ({
                      ...mark,
                      label: t(mark.label),
                    }))}
                  />
                </Box>
              )}
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t('From')}
                  value={dateFrom ? dayjs(dateFrom) : null}
                  onChange={(value: Dayjs | null) =>
                    setDateFrom(
                      value?.isValid() ? value.format('YYYY-MM-DD') : '',
                    )
                  }
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label={t('To')}
                  value={dateTo ? dayjs(dateTo) : null}
                  onChange={(value: Dayjs | null) =>
                    setDateTo(
                      value?.isValid() ? value.format('YYYY-MM-DD') : '',
                    )
                  }
                  slotProps={{ textField: { size: 'small' } }}
                />
              </LocalizationProvider>
            </Stack>

            <Stack alignItems={{ xs: 'stretch', md: 'flex-end' }} spacing={3}>
              <Link
                component="button"
                underline="hover"
                onClick={handleClearFilters}
                sx={{ fontSize: 14 }}
              >
                {t('Clear Filters')}
              </Link>
              <Button
                variant="contained"
                size="small"
                disabled={loadingReport}
                onClick={handleGenerate}
              >
                {loadingReport ? t('Generating') : t('Generate')}
              </Button>
            </Stack>
          </Box>

          <Box
            sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              size="small"
              disabled={!report || downloading}
              onClick={handleDownload}
              startIcon={
                downloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DownloadOutlinedIcon />
                )
              }
            >
              {downloading ? t('Downloading') : t('Download')}
            </Button>
          </Box>

          <Box sx={{ mt: 2, height: 480, width: '100%' }}>
            <DataGridPro
              apiRef={apiRef}
              columnHeaderHeight={56}
              sx={{
                border: 'none',
                '.MuiDataGrid-row:nth-of-type(even):not(.Mui-selected)': {
                  backgroundColor: '#ECECEC',
                },
                '.MuiDataGrid-row.Mui-selected': {
                  backgroundColor: (theme) =>
                    hexToRGBA(theme.palette.primary.main, 0.19),
                },
                '.MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                },
                '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                  outline: 'none !important',
                },
              }}
              rows={rows.map((row, index) => ({
                ...row,
                id: `${row.vendorCode}-${row.invoiceCode}-${index}`,
              }))}
              loading={loadingReport}
              columns={columns}
              columnVisibilityModel={persistedPrefs.columnVisibilityModel}
              onColumnVisibilityModelChange={
                persistedPrefs.onColumnVisibilityModelChange
              }
              onColumnWidthChange={persistedPrefs.onColumnWidthChange}
              onColumnOrderChange={persistedPrefs.onColumnOrderChange}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={(model) => {
                setPaginationModel(model);
                loadReport(buildReportInput(model.page, model.pageSize));
              }}
              rowCount={report?.total ?? 0}
              pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
              disableRowSelectionOnClick
              disableColumnFilter
              autoHeight={false}
              localeText={{
                noRowsLabel: report
                  ? t('No accounts payable rows found')
                  : t('Generate the report to view accounts payable'),
              }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
